/* Accès aux données. Une requête = un hook, sans bibliothèque de cache :
   le volume tient en mémoire et les écrans sont peu nombreux. Si le besoin
   de cache partagé apparaît, c'est ici qu'on branchera TanStack Query. */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import type {
  Client, CommandeUsine, Commission, Contact, DemandePrix, DocumentUsine, DossierPackaging,
  EntreeJournal, LigneCommandeUsine, ListePrix, ModeleDocument,
  LigneVente, PartAgent, PrixAgent, PrixClient, PrixUsine, Produit, ProduitUsine, Profil,
  PropositionPrix, Reclamation, RegleCommission, RegleSurplus, SujetJournal, Usine, Vente,
} from "@/types/metier";

export interface Etat<T> {
  donnees: T;
  chargement: boolean;
  erreur: string | null;
  recharger: () => void;
}

function useRequete<T>(
  nom: string,
  executer: () => PromiseLike<{ data: T | null; error: { message: string } | null }>,
  defaut: T,
): Etat<T> {
  const [donnees, setDonnees] = useState<T>(defaut);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const fn = useRef(executer);
  fn.current = executer;

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    Promise.resolve(fn.current()).then(({ data, error }) => {
      if (!vivant) return;
      if (error) {
        setErreur(`${nom} : ${error.message}`);
      } else {
        setErreur(null);
        setDonnees((data ?? defaut) as T);
      }
      setChargement(false);
    });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nom, tick]);

  const recharger = useCallback(() => setTick((t) => t + 1), []);
  return { donnees, chargement, erreur, recharger };
}

/* On lit la vue, pas la table. C'est la base qui décide de ce que ce niveau a
   le droit de voir : un junior y trouve la catégorie à la place du nom, et
   aucune coordonnée. Le masquage ne dépend plus de l'écran qui l'affiche. */
export const useUsines = () =>
  useRequete<Usine[]>(
    "usines",
    () => supabase.from("usines_lisibles").select("*").order("nom"),
    [],
  );

export const useProduits = () =>
  useRequete<Produit[]>(
    "produits",
    () => supabase.from("produits").select("*").order("ref").limit(2000),
    [],
  );

export const useClients = () =>
  useRequete<Client[]>(
    "clients",
    () => supabase.from("clients").select("*").order("nom"),
    [],
  );

/* Le déploiement et la migration ne tombent pas à la même seconde : Cloudflare
   reconstruit dès l'envoi, la migration se pousse à la main. Entre les deux, la
   base ignore encore `compte_id` et toute requête qui le nomme échoue — ce qui
   suffirait à mettre tout le monde dehors. On retombe donc sur l'ancien jeu de
   colonnes quand Postgres dit qu'il n'en connaît pas une (42703).

   À retirer quand la migration 20261026120000 sera posée partout. */
const COLONNES_PROFIL =
  "id, nom, email, niveau, actif, compte_id, invite_le, telephone, portee, usine_id";
const COLONNES_PROFIL_AVANT = "id, nom, email, niveau, actif";

export function colonneAbsente(
  e: { code?: string; message?: string } | null | undefined,
): boolean {
  if (!e) return false;
  // 42703 est le code Postgres ; le message sert de filet si PostgREST change.
  return e.code === "42703" || /column .* does not exist/i.test(e.message ?? "");
}

/* Les agents, et eux seuls : depuis l'ouverture aux usines, `profils` porte
   aussi les comptes fournisseurs, qu'on ne veut voir ni dans la liste des
   agents ni dans les listes déroulantes d'affectation. */
export const useAgents = () =>
  useRequete<Profil[]>(
    "agents",
    async () => {
      const r = await supabase
        .from("profils").select(COLONNES_PROFIL).eq("portee", "omnix").order("nom");
      if (!colonneAbsente(r.error)) return r;
      return supabase.from("profils").select(COLONNES_PROFIL_AVANT).order("nom");
    },
    [],
  );

/** Les comptes ouverts à une usine. Rien que pour la direction. */
export const useAccesUsine = (usineId: string | undefined) =>
  useRequete<Profil[]>(
    `accès usine ${usineId ?? ""}`,
    async () => {
      if (!usineId) return { data: [], error: null };
      const r = await supabase
        .from("profils").select(COLONNES_PROFIL)
        .eq("portee", "usine").eq("usine_id", usineId).order("nom");
      if (colonneAbsente(r.error)) return { data: [], error: null };
      return r;
    },
    [],
  );

/* Le prix usine : la direction seule reçoit ces lignes. Pour les autres la
   requête réussit et rend un tableau vide — c'est voulu, l'écran n'a pas à
   demander la permission avant de demander la donnée.

   `histoire` ramène aussi les périodes closes : les écrans qui ne montrent
   que le prix du jour s'en passent, et c'est beaucoup moins de lignes. */
export const usePrixUsine = (histoire = false) =>
  useRequete<PrixUsine[]>(
    histoire ? "prix usine (archives)" : "prix usine",
    () => {
      const q = supabase.from("prix_usine").select("*").limit(8000);
      return histoire ? q.order("debut_le", { ascending: false }) : q.is("fin_le", null);
    },
    [],
  );

export const usePrixAgent = (histoire = false) =>
  useRequete<PrixAgent[]>(
    histoire ? "prix agents (archives)" : "prix agents",
    () => {
      const q = supabase.from("prix_agent").select("*").limit(8000);
      return histoire ? q.order("debut_le", { ascending: false }) : q.is("fin_le", null);
    },
    [],
  );

export const usePrixClient = (histoire = false) =>
  useRequete<PrixClient[]>(
    histoire ? "tarifs clients (archives)" : "tarifs clients",
    () => {
      const q = supabase.from("prix_client").select("*").limit(8000);
      return histoire ? q.order("debut_le", { ascending: false }) : q.is("fin_le", null);
    },
    [],
  );

/** Une référence et son usine, pour sa fiche. */
export const useProduit = (id: string | undefined) =>
  useRequete<Produit | null>(
    `produit ${id ?? ""}`,
    async () => {
      if (!id) return { data: null, error: null };
      const { data, error } = await supabase
        .from("produits").select("*").eq("id", id).maybeSingle();
      return { data: (data as Produit | null) ?? null, error };
    },
    null,
  );

/** Tout ce qui a été vendu d'une référence, vente par vente. */
export const useLignesProduit = (produitId: string | undefined) =>
  useRequete<(LigneVente & { vente: Vente | null })[]>(
    `lignes produit ${produitId ?? ""}`,
    () => {
      if (!produitId) return Promise.resolve({ data: [], error: null });
      return supabase
        .from("lignes_vente")
        .select("*, vente:ventes(*)")
        .eq("produit_id", produitId)
        .limit(500);
    },
    [],
  );

/** Les documents rattachés à une référence précise, toutes usines confondues. */
export const useDocumentsProduit = (produitId: string | undefined) =>
  useRequete<DocumentUsine[]>(
    `documents produit ${produitId ?? ""}`,
    () => {
      if (!produitId) return Promise.resolve({ data: [], error: null });
      return supabase.from("documents_usine").select("*").eq("produit_id", produitId);
    },
    [],
  );

export const useDocumentsUsine = (usineId?: string) =>
  useRequete<DocumentUsine[]>(
    `documents usine ${usineId ?? ""}`,
    () => {
      if (!usineId) return Promise.resolve({ data: [], error: null });
      return supabase
        .from("documents_usine")
        .select("*")
        .eq("usine_id", usineId)
        .order("cree_le", { ascending: false });
    },
    [],
  );

/* Le fil d'une fiche. Il n'est jamais préchargé en masse : une fiche à la
   fois, et la table est indexée là-dessus. */
export const useJournal = (sujet: SujetJournal | undefined, sujetId: string | undefined) =>
  useRequete<EntreeJournal[]>(
    `journal ${sujet ?? ""} ${sujetId ?? ""}`,
    () => {
      if (!sujet || !sujetId) return Promise.resolve({ data: [], error: null });
      return supabase
        .from("journal")
        .select("*")
        .eq("sujet", sujet)
        .eq("sujet_id", sujetId)
        .order("cree_le", { ascending: false })
        .limit(400);
    },
    [],
  );

/** Tout ce qui reste à faire, tous sujets confondus — pour le tableau de bord. */
export const useActivites = () =>
  useRequete<EntreeJournal[]>(
    "activités",
    () =>
      supabase
        .from("journal")
        .select("*")
        .eq("nature", "activite")
        .eq("etat", "a_faire")
        .order("echeance", { ascending: true })
        .limit(300),
    [],
  );

export const useDemandesPrix = () =>
  useRequete<DemandePrix[]>(
    "demandes de prix",
    () => supabase.from("demandes_prix").select("*").order("cree_le", { ascending: false }),
    [],
  );

export const useReglesSurplus = () =>
  useRequete<RegleSurplus[]>(
    "règles d'excédent",
    () => supabase.from("regles_surplus").select("*"),
    [],
  );

export const useVentes = () =>
  useRequete<Vente[]>(
    "ventes",
    () =>
      supabase
        .from("ventes")
        .select("*, lignes:lignes_vente(*)")
        .order("date_document", { ascending: false }),
    [],
  );

/** Une vente et ses lignes, pour sa fiche. */
export const useVente = (id: string | undefined) =>
  useRequete<Vente | null>(
    `vente ${id ?? ""}`,
    async () => {
      if (!id) return { data: null, error: null };
      const { data, error } = await supabase
        .from("ventes")
        .select("*, lignes:lignes_vente(*)")
        .eq("id", id)
        .maybeSingle();
      return { data: (data as Vente | null) ?? null, error };
    },
    null,
  );

/** La commission rattachée à une vente — il n'y en a jamais plus d'une. */
export const useCommissionVente = (venteId: string | undefined) =>
  useRequete<Commission | null>(
    `commission de ${venteId ?? ""}`,
    async () => {
      if (!venteId) return { data: null, error: null };
      const { data, error } = await supabase
        .from("commissions")
        .select("*")
        .eq("vente_id", venteId)
        .maybeSingle();
      return { data: (data as Commission | null) ?? null, error };
    },
    null,
  );

export const useCommissions = () =>
  useRequete<Commission[]>(
    "commissions",
    () => supabase.from("commissions").select("*").order("reference", { ascending: false }),
    [],
  );

export const useRegles = () =>
  useRequete<RegleCommission[]>(
    "barème",
    () => supabase.from("regles_commission").select("*").order("usine_id"),
    [],
  );

export const useParts = () =>
  useRequete<PartAgent[]>(
    "parts agents",
    () => supabase.from("parts_agent").select("*"),
    [],
  );

export const useUsine = (id: string | undefined) =>
  useRequete<Usine | null>(
    `usine ${id ?? ""}`,
    async () => {
      if (!id) return { data: null, error: null };
      const { data, error } = await supabase
        .from("usines_lisibles").select("*").eq("id", id).maybeSingle();
      return { data: (data as Usine | null) ?? null, error };
    },
    null,
  );

/* ─────────────────────────── l'espace d'une usine ─────────────────────────
   Quatre lectures, et elles ne portent jamais ni le prix de vente d'Omnix ni
   le montant facturé au client : ce sont des vues, pas des tables. */

/** Sa propre fiche, telle qu'elle la lit. */
export const useMonUsine = (id: string | undefined) =>
  useRequete<Usine | null>(
    `mon usine ${id ?? ""}`,
    async () => {
      if (!id) return { data: null, error: null };
      const { data, error } = await supabase.from("usines").select("*").eq("id", id).maybeSingle();
      return { data: (data as Usine | null) ?? null, error };
    },
    null,
  );

export const useMesProduits = () =>
  useRequete<ProduitUsine[]>(
    "mes références",
    () => supabase.from("produits_usine").select("*").order("ref").limit(2000),
    [],
  );

export const useMesCommandes = () =>
  useRequete<CommandeUsine[]>(
    "mes commandes",
    () => supabase.from("commandes_usine").select("*").order("date_document", { ascending: false }),
    [],
  );

export const useMesLignesCommande = () =>
  useRequete<LigneCommandeUsine[]>(
    "lignes de mes commandes",
    () => supabase.from("lignes_commande_usine").select("*").limit(4000),
    [],
  );

/** Les prix annoncés : toutes pour la direction, les siennes pour l'usine. */
export const usePropositions = () =>
  useRequete<PropositionPrix[]>(
    "propositions de prix",
    () => supabase.from("propositions_prix").select("*").order("cree_le", { ascending: false }),
    [],
  );

export const useClient = (id: string | undefined) =>
  useRequete<Client | null>(
    `client ${id ?? ""}`,
    async () => {
      if (!id) return { data: null, error: null };
      const { data, error } = await supabase.from("clients").select("*").eq("id", id).maybeSingle();
      return { data: (data as Client | null) ?? null, error };
    },
    null,
  );

export const usePackaging = () =>
  useRequete<DossierPackaging[]>(
    "packaging",
    () => supabase.from("dossiers_packaging").select("*").order("reference", { ascending: false }),
    [],
  );

export const useReclamations = () =>
  useRequete<Reclamation[]>(
    "qualité",
    () => supabase.from("reclamations").select("*").order("ouverte_le", { ascending: false }),
    [],
  );

export const useListesPrix = () =>
  useRequete<ListePrix[]>(
    "listes de prix",
    () => supabase.from("listes_prix").select("*").order("reference", { ascending: false }),
    [],
  );

export const useModeles = () =>
  useRequete<ModeleDocument[]>(
    "modèles de document",
    () => supabase.from("modeles_document").select("*"),
    [],
  );

/** Les contacts d'une usine, ou de toutes quand aucune n'est donnée. */
export const useContactsUsine = (usineId?: string) =>
  useRequete<Contact[]>(
    `contacts usine ${usineId ?? "toutes"}`,
    async () => {
      let q = supabase.from("contacts_usine").select("*").order("nom");
      if (usineId) q = q.eq("usine_id", usineId);
      const { data, error } = await q;
      return { data: (data as Contact[] | null) ?? [], error };
    },
    [],
  );

export const useContactsClient = (clientId?: string) =>
  useRequete<Contact[]>(
    `contacts client ${clientId ?? "tous"}`,
    async () => {
      let q = supabase.from("contacts_client").select("*").order("nom");
      if (clientId) q = q.eq("client_id", clientId);
      const { data, error } = await q;
      return { data: (data as Contact[] | null) ?? [], error };
    },
    [],
  );

/** Un réglage de l'application (transparence des commissions, série…). */
export async function lireParametre<T>(cle: string, defaut: T): Promise<T> {
  const { data, error } = await supabase
    .from("parametres")
    .select("valeur")
    .eq("cle", cle)
    .maybeSingle();
  if (error || !data) return defaut;
  return (data.valeur as T) ?? defaut;
}

export async function ecrireParametre(cle: string, valeur: unknown): Promise<void> {
  await supabase.from("parametres").upsert({ cle, valeur });
}
